/**
 * Client logout — the single implementation.
 *
 * Auth state lives in exactly two localStorage keys. App.js `getInitialView()`
 * re-hydrates the logged-in view from both of them on mount, so a logout that
 * leaves either one behind is not a logout.
 *
 * The ordering below is the whole point: storage is cleared SYNCHRONOUSLY,
 * before the network call and before any caller navigates. Callers redirect
 * immediately after this resolves — and a redirect tears down the document, so
 * anything sequenced after an `await` may simply never run. Clearing first is
 * what makes logout survive that. (JP-536: three separate implementations each
 * cleared storage after an await, or cleared the wrong keys, and logout took
 * two clicks.)
 */

export const AUTH_TOKEN_KEY = 'jyotish_session_token';
export const AUTH_USER_KEY = 'jyotish_auth_user';

// Legacy keys written by older builds. Removed on logout so a stale guest or
// test identity can't outlive the session that replaced it.
const LEGACY_KEYS = ['auth_token', 'user_id', 'guest_user_id', 'test_user_id'];

/**
 * Remove every client-side trace of the session. Synchronous by contract —
 * never make this async, and never await anything before calling it.
 */
export const clearStoredAuth = (storage = window.localStorage) => {
  try {
    storage.removeItem(AUTH_TOKEN_KEY);
    storage.removeItem(AUTH_USER_KEY);
    LEGACY_KEYS.forEach((key) => storage.removeItem(key));
  } catch (e) {
    console.warn('[LOGOUT] Failed to clear stored auth:', e);
  }
};

/**
 * Full logout sequence: clear client state, then tell the server to drop the
 * session row. Never throws — a failed network call must not leave the user
 * looking logged in.
 *
 * Deps are injectable for tests; production callers pass only `apiUrl`.
 */
export const performLogout = async ({
  apiUrl,
  // Wrapped, not passed as a bare reference: a detached `fetch` loses its
  // window binding and can throw "Illegal invocation".
  fetchImpl = typeof fetch !== 'undefined' ? (...args) => fetch(...args) : null,
  storage = window.localStorage,
  session = window.sessionStorage,
} = {}) => {
  // Read the token before clearing — the server needs it to identify the
  // session when the cookie doesn't reach us (Safari ITP, blocked third-party
  // cookies, embedded webviews). Backend accepts either.
  let token = null;
  try {
    token = storage.getItem(AUTH_TOKEN_KEY);
  } catch (e) {
    /* storage unavailable — proceed cookie-only */
  }

  clearStoredAuth(storage);

  try {
    session.clear();
  } catch (e) {
    /* non-fatal */
  }

  if (!fetchImpl) return;

  try {
    await fetchImpl(`${apiUrl}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (e) {
    // Server-side invalidation failed; client state is already gone, which is
    // what the user asked for. Surfacing an error here would only block them.
    console.error('[LOGOUT] Server logout failed:', e);
  }
};
